/**
 * As chamadas da inscrição pública (F05) — perfis e veículos do SGTP, escolas
 * do EOL, rascunho, envio e comprovante. Contrato em
 * `docs/credenciamento-ponta-a-ponta/frentes/F05.md`, seção 2.
 *
 * Toda rota é do inscrito (F04): sem token do Keycloak, com o cookie
 * `HttpOnly` da sessão. Por isso `publica: true` e `credenciais: true` em
 * todas. O CPF completo nunca passa por aqui — o perfil pessoa física é a
 * chave `PF`, e o servidor sabe o CPF pela sessão.
 */

import { useQuery } from '@tanstack/react-query';

import { chamarApi, ErroApi } from '@/nucleo/auth/cliente-api';

const BASE = '/bff/v1/inscricao-publica';

export const MODALIDADES_DA_INSCRICAO = [
  'CONVENCIONAL',
  'ACESSIVEL',
  'ESPECIAL',
  'CRECHE',
] as const;
export type ModalidadeDaInscricao = (typeof MODALIDADES_DA_INSCRICAO)[number];

export type TipoDeEntidade = 'PF' | 'PJ' | 'COOPERATIVA';

export interface CicloDaInscricao {
  id: string;
  nome: string;
  fimEm: string;
  editalVersao: string;
  editalDisponivel: boolean;
  maxEscolas: number;
  dres: { id: string; codigo: string; nome: string }[];
  modalidades: ModalidadeDaInscricao[];
}

export interface EstadoDoPeriodo {
  aceitaInscricoes: boolean;
  motivo: 'ABERTO' | 'NENHUM_CICLO' | 'AGUARDANDO_INICIO' | 'ENCERRADO';
  mensagem: string;
}

export type SituacaoDaEntidade = 'DISPONIVEL' | 'RASCUNHO' | 'INSCRITA';

export interface PerfilDoInscrito {
  chave: string;
  tipo: TipoDeEntidade;
  documento: string;
  nome: string;
  contatos: { email: string | null; celular: string | null };
  situacao: SituacaoDaEntidade;
  submissaoId: string | null;
  protocolos: string[];
}

export interface PerfisDoInscrito {
  periodo: EstadoDoPeriodo;
  ciclo: CicloDaInscricao | null;
  perfis: PerfilDoInscrito[];
}

export type MotivoDeVeiculoIndisponivel =
  'LICENCA_IRREGULAR' | 'LICENCA_VENCIDA' | 'JA_INSCRITO_NO_CICLO';

export interface VeiculoDoPerfil {
  placa: string;
  numeroLicenca: string;
  situacaoLicenca: 'REGULAR' | 'IRREGULAR' | 'VENCIDA';
  validadeLicenca: string;
  capacidade: number;
  anoFabricacao: number;
  tipo: string;
  modelo: string;
  acessivel: boolean;
  selecionavel: boolean;
  motivo: MotivoDeVeiculoIndisponivel | null;
}

export interface EscolaDaInscricao {
  codigoEol: string;
  nome: string;
  tipoUnidade: string;
  endereco: string | null;
}

export interface CooperativaDaInscricao {
  cnpj: string;
  nome: string;
}

export interface VeiculoDoRascunho {
  placa: string;
  modalidade: ModalidadeDaInscricao | null;
  dreId: string | null;
  /** Código EOL, em ordem de preferência (a primeira é a 1ª opção). */
  escolas: string[];
  cooperativaCnpj: string | null;
}

export interface DadosDoRascunho {
  veiculos: VeiculoDoRascunho[];
  aceiteEdital: boolean;
  aceiteLgpd: boolean;
  editalVersao: string | null;
}

export interface ContatosDoRascunho {
  email: string | null;
  telefone: string | null;
}

export interface Rascunho {
  id: string;
  versao: number;
  etapa: number;
  ciclo: CicloDaInscricao | null;
  entidade: { tipo: TipoDeEntidade; documento: string; nome: string };
  contatos: ContatosDoRascunho;
  dados: DadosDoRascunho;
  lgpdVersao: string;
  atualizadoEm: string;
}

export type ResultadoDoInicio =
  | { resultado: 'CRIADO' | 'RETOMADO'; rascunho: Rascunho }
  | { resultado: 'INSCRICAO_JA_ENVIADA'; mensagem: string; protocolos: string[] }
  | { resultado: 'CICLO_FECHADO'; mensagem: string };

export interface VeiculoDoComprovante {
  protocolo: string;
  placa: string;
  numeroLicenca: string | null;
  modalidade: ModalidadeDaInscricao;
  dre: { id: string; nome: string };
  escolas: { codigoEol: string; nome: string; prioridade: number }[];
  cooperativa: { cnpj: string; nome: string } | null;
}

export interface Comprovante {
  submissaoId: string;
  enviadoEm: string;
  ciclo: { id: string; nome: string };
  entidade: { tipo: TipoDeEntidade; documento: string; nome: string };
  contatos: { email: string; telefone: string | null };
  editalVersao: string;
  lgpdVersao: string;
  veiculos: VeiculoDoComprovante[];
  sha256: string;
  linkAcompanhamento: string;
}

export type CampoDaPendencia =
  | 'CICLO'
  | 'ENTIDADE'
  | 'CONTATOS'
  | 'VEICULOS'
  | 'VEICULO'
  | 'MODALIDADE'
  | 'DRE'
  | 'ESCOLAS'
  | 'COOPERATIVA'
  | 'EDITAL'
  | 'LGPD';

export interface Pendencia {
  campo: CampoDaPendencia;
  placa: string | null;
  mensagem: string;
}

export type ResultadoDoEnvio =
  | { resultado: 'ENVIADA'; repetido: boolean; comprovante: Comprovante }
  | { resultado: 'PENDENCIAS'; mensagem: string; pendencias: Pendencia[] };

function chamar<T>(caminho: string, metodo: 'GET' | 'POST' | 'PUT' = 'GET', corpo?: unknown) {
  return chamarApi<T>(`${BASE}${caminho}`, {
    metodo,
    corpo,
    publica: true,
    credenciais: true,
  });
}

export const apiDaInscricao = {
  perfis: () => chamar<PerfisDoInscrito>('/perfis'),
  veiculos: (perfil: string) =>
    chamar<VeiculoDoPerfil[]>(`/perfis/${encodeURIComponent(perfil)}/veiculos`),
  escolas: (dreId: string, modalidade: ModalidadeDaInscricao) =>
    chamar<EscolaDaInscricao[]>(
      `/escolas?dreId=${encodeURIComponent(dreId)}&modalidade=${encodeURIComponent(modalidade)}`,
    ),
  cooperativas: () => chamar<CooperativaDaInscricao[]>('/cooperativas'),
  iniciar: (perfil: string) => chamar<ResultadoDoInicio>('/rascunhos', 'POST', { perfil }),
  salvar: (
    id: string,
    entrada: {
      versao: number;
      etapa: number;
      dados: DadosDoRascunho;
      contatos: ContatosDoRascunho;
    },
  ) => chamar<Rascunho>(`/rascunhos/${id}`, 'PUT', entrada),
  enviar: (id: string, versao: number, chaveIdempotencia: string) =>
    chamar<ResultadoDoEnvio>(`/rascunhos/${id}/enviar`, 'POST', { versao, chaveIdempotencia }),
  comprovante: (protocolo: string) =>
    chamar<Comprovante>(`/comprovantes/${encodeURIComponent(protocolo)}`),
};

/** Endereço do PDF do edital do ciclo (rota pública de F03). */
export function enderecoDoEdital(cicloId: string, apiBase: string): string {
  return `${apiBase}/bff/v1/ciclo-inscricao-publico/edital/${encodeURIComponent(cicloId)}`;
}

export const CHAVES_DA_INSCRICAO = {
  /** Prefixo de todas: sair da sessão esquece a inscrição inteira desta aba. */
  todas: ['inscricao-publica'] as const,
  perfis: ['inscricao-publica', 'perfis'] as const,
  veiculos: (perfil: string) => ['inscricao-publica', 'veiculos', perfil] as const,
  escolas: (dreId: string, modalidade: string) =>
    ['inscricao-publica', 'escolas', dreId, modalidade] as const,
  cooperativas: ['inscricao-publica', 'cooperativas'] as const,
  comprovante: (protocolo: string) => ['inscricao-publica', 'comprovante', protocolo] as const,
};

/**
 * O estado público do período de inscrição (rota anônima de F03). A tela
 * consulta ANTES do portão: sem período aberto não há por que pedir identidade,
 * e a pessoa precisa saber o porquê (RN-CP-03).
 */
export function useEstadoDoPeriodo() {
  return useQuery({
    queryKey: ['inscricao-publica', 'estado-do-periodo'] as const,
    queryFn: () =>
      chamarApi<EstadoDoPeriodo & { agora: string }>('/bff/v1/ciclo-inscricao-publico/estado', {
        publica: true,
      }),
    retry: false,
    staleTime: 60_000,
  });
}

export function usePerfisDoInscrito(habilitado = true) {
  return useQuery({
    queryKey: CHAVES_DA_INSCRICAO.perfis,
    queryFn: apiDaInscricao.perfis,
    enabled: habilitado,
    retry: false,
  });
}

export function useVeiculosDoPerfil(perfil: string) {
  return useQuery({
    queryKey: CHAVES_DA_INSCRICAO.veiculos(perfil),
    queryFn: () => apiDaInscricao.veiculos(perfil),
    retry: false,
  });
}

export function useEscolasDaInscricao(
  dreId: string | null,
  modalidade: ModalidadeDaInscricao | null,
) {
  return useQuery({
    queryKey: CHAVES_DA_INSCRICAO.escolas(dreId ?? '', modalidade ?? ''),
    queryFn: () => apiDaInscricao.escolas(dreId!, modalidade!),
    enabled: Boolean(dreId && modalidade),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useCooperativasDaInscricao() {
  return useQuery({
    queryKey: CHAVES_DA_INSCRICAO.cooperativas,
    queryFn: apiDaInscricao.cooperativas,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useComprovante(protocolo: string) {
  return useQuery({
    queryKey: CHAVES_DA_INSCRICAO.comprovante(protocolo),
    queryFn: () => apiDaInscricao.comprovante(protocolo),
    retry: false,
  });
}

/** A frase da API para a tela, ou a genérica — nunca erro técnico. */
export function mensagemDoErro(erro: unknown, generica: string): string {
  return erro instanceof ErroApi ? erro.aviso : generica;
}
